import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { NoteContent } from '@/components/NoteContent';
import { CautionCorner } from '@/components/jp/DinoArt';
import { cn } from '@/lib/utils';

interface NoteCardProps {
  event: NostrEvent;
  index?: number;
}

/* ------------------------------ helpers ------------------------------ */

function formatTimestamp(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate(),
  )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
    date.getUTCSeconds(),
  )}Z`;
}

function timeAgo(unixSeconds: number): string {
  const diff = Math.max(1, Math.floor(Date.now() / 1000) - unixSeconds);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400 / 7)}w`;
  return `${Math.floor(diff / 86400 / 30)}mo`;
}

/** Deterministic 4-digit "Paddock" id per author — pure flavour. */
function paddockId(pubkey: string): string {
  let hash = 0;
  for (let i = 0; i < pubkey.length; i++) {
    hash = (hash * 31 + pubkey.charCodeAt(i)) >>> 0;
  }
  return `P-${String(hash % 10000).padStart(4, '0')}`;
}

/** Deterministic "threat level" label for flavour badge. */
function threatLevel(pubkey: string): { label: string; tone: 'ok' | 'warn' | 'danger' } {
  const sum = [...pubkey].reduce((a, c) => a + c.charCodeAt(0), 0);
  const n = sum % 10;
  if (n < 6) return { label: 'STABLE', tone: 'ok' };
  if (n < 9) return { label: 'ELEVATED', tone: 'warn' };
  return { label: 'CAUTION', tone: 'danger' };
}

/* ------------------------------ component ---------------------------- */

export function NoteCard({ event, index = 0 }: NoteCardProps) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;

  const displayName = metadata?.name ?? genUserName(event.pubkey);
  const picture = metadata?.picture;
  const npub = nip19.npubEncode(event.pubkey);
  const noteId = nip19.noteEncode(event.id);

  const transmissionIndex = String(index + 1).padStart(4, '0');
  const paddock = paddockId(event.pubkey);
  const threat = threatLevel(event.pubkey);

  return (
    <article
      className={cn(
        'jp-panel jp-fade-in relative overflow-hidden rounded-sm',
        'transition-[box-shadow,transform] duration-300',
        'hover:shadow-[0_6px_26px_hsl(40_95%_55%_/_0.18)]',
      )}
    >
      {/* Corner chevrons — four caution markers */}
      <CautionCorner className="absolute top-1 left-1 w-4 h-4 text-jp-amber/70" />
      <CautionCorner className="absolute top-1 right-1 w-4 h-4 text-jp-amber/70 rotate-90" />
      <CautionCorner className="absolute bottom-1 left-1 w-4 h-4 text-jp-amber/70 -rotate-90" />
      <CautionCorner className="absolute bottom-1 right-1 w-4 h-4 text-jp-amber/70 rotate-180" />

      {/* Transmission header strip */}
      <header
        className={cn(
          'jp-terminal-strip flex items-center justify-between px-3 py-1.5',
        )}
      >
        <div className="flex items-center gap-2">
          <span className="jp-led" aria-hidden />
          <span className="font-mono">TRANSMISSION #{transmissionIndex}</span>
          <span className="opacity-40">//</span>
          <span className="font-mono opacity-75">{paddock}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'font-mono text-[0.65rem] px-1.5 py-0.5 border',
              threat.tone === 'ok' &&
                'border-green-500/50 text-green-400',
              threat.tone === 'warn' &&
                'border-jp-amber/60 text-jp-amber-bright',
              threat.tone === 'danger' &&
                'border-jp-red/60 text-jp-red animate-pulse',
            )}
          >
            LVL · {threat.label}
          </span>
          <time
            className="font-mono opacity-70"
            title={formatTimestamp(event.created_at)}
          >
            T-{timeAgo(event.created_at)}
          </time>
        </div>
      </header>

      {/* Body */}
      <div className="relative px-4 pt-3 pb-4">
        {/* Author row */}
        <div className="flex items-start gap-3">
          <Link
            to={`/${npub}`}
            className="relative shrink-0 group"
            aria-label={`View profile of ${displayName}`}
          >
            <div
              className={cn(
                'size-12 rounded-sm overflow-hidden',
                'ring-1 ring-jp-amber/60 ring-offset-2 ring-offset-[hsl(120_20%_7%)]',
                'bg-gradient-to-br from-jp-jungle to-black',
              )}
            >
              {picture ? (
                <img
                  src={picture}
                  alt={displayName}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[0.25] saturate-[0.95] contrast-110 group-hover:grayscale-0 transition-[filter] duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-jp-amber font-display text-lg">
                  {displayName[0]?.toUpperCase() ?? '?'}
                </div>
              )}
            </div>
            {/* Clearance tag badge */}
            <span className="absolute -bottom-1 -right-1 bg-jp-amber text-[hsl(120_30%_6%)] font-mono text-[0.55rem] px-1 leading-tight border border-black/50">
              CL-A
            </span>
          </Link>

          <div className="flex-1 min-w-0">
            <Link
              to={`/${npub}`}
              className="font-display tracking-wide text-jp-bone text-lg leading-tight hover:text-jp-amber-bright transition-colors truncate block"
            >
              {displayName}
            </Link>
            <div className="flex items-center gap-2 font-mono text-[0.7rem] text-jp-amber/70">
              <span className="truncate">
                {metadata?.nip05 ?? `${npub.slice(0, 12)}…${npub.slice(-4)}`}
              </span>
            </div>
            {metadata?.about && (
              <p className="mt-1 text-[0.72rem] text-muted-foreground line-clamp-1 italic">
                “{metadata.about}”
              </p>
            )}
          </div>
        </div>

        {/* Divider — dashed amber (chain-link stand-in) */}
        <div
          className="my-3 h-px"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, hsl(40 95% 55% / 0.6) 0, hsl(40 95% 55% / 0.6) 6px, transparent 6px, transparent 12px)',
          }}
          aria-hidden
        />

        {/* Content label */}
        <div className="font-mono text-[0.65rem] tracking-[0.2em] text-jp-amber/70 mb-2">
          ◉ FIELD REPORT
        </div>

        {/* Message content */}
        <div className="text-jp-bone text-[0.95rem] leading-relaxed font-typewriter">
          <NoteContent event={event} />
        </div>

        {/* Footer metadata row */}
        <footer className="mt-4 pt-3 border-t border-dashed border-jp-amber/25 flex items-center justify-between font-mono text-[0.65rem] text-jp-amber/70">
          <div className="flex items-center gap-3">
            <span>⧉ SIG</span>
            <code className="opacity-70">
              {event.id.slice(0, 10)}…{event.id.slice(-4)}
            </code>
          </div>
          <Link
            to={`/${noteId}`}
            className={cn(
              'px-2 py-0.5 border border-jp-amber/50 text-jp-amber-bright',
              'hover:bg-jp-amber hover:text-[hsl(120_30%_6%)] transition-colors',
            )}
          >
            VIEW LOG ▸
          </Link>
        </footer>
      </div>
    </article>
  );
}
